import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PaymentPageConfig } from '@front-platform/content-builder';
import { paymentPageResolveConfig } from '@front-platform/content-builder';
import { combineLatest, of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, switchMap, take, takeUntil } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, input } from '@angular/core';

import { OrderUtils } from '@app/utilities/order.util';
import { DoodOrderModel, DoodValidateOrderQuery } from '@store/order/order.model';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { PaymentMethodHandles } from '@config/payment-methods.config';

import { OrderStoreSelector } from '@common/selectors/order.selector';
import { BasketStoreRefiner } from '@common/refiners/basket.refiner';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';

import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { PaymentService } from '@core/services/payment/payment.service';
import { OrderPaymentsApiService } from '@core/services/api/order-payments/order-payments-api.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { TransactionsService } from '@core/services/transactions/transactions.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';

/** Handles that trigger an immediate redirect to an external payment page (no form on our side). */
const REDIRECT_PAYMENT_HANDLES = new Set<string>([
  PaymentMethodHandles.Worldline,
  PaymentMethodHandles.Yavin,
]);
export interface PaymentPageRuntimeConfig {
  'body'?: unknown[];
}

export type PaymentPageConfigInput = Partial<
  Omit<PaymentPageConfig, keyof PaymentPageRuntimeConfig> & PaymentPageRuntimeConfig
>;
export type PaymentPageResolvedConfig = Omit<PaymentPageConfig, keyof PaymentPageRuntimeConfig> &
  Partial<PaymentPageRuntimeConfig>;
export interface PaymentPageContract extends ConfigComponentContract<
  PaymentPageConfigInput,
  PaymentPageResolvedConfig
> {}

@Component({
  selector: 'app-payment-page',
  templateUrl: './payment-page.component.html',
  standalone: false,
})
export class PaymentPageComponent
  extends DestroyerBase
  implements OnInit, OnDestroy, PaymentPageContract
{
  readonly config = input<PaymentPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<PaymentPageConfig, PaymentPageRuntimeConfig>(
    paymentPageResolveConfig,
    () => ({ config: this.config() }),
  );
  get body(): unknown[] {
    return this.resolvedConfig()['body'] as unknown as unknown[];
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get backgroundImageCss(): string | null {
    const url = this.backgroundImage?.url;
    return url ? `url(${url})` : null;
  }

  orderValidate$ = this.orderSelector.selectValidation$;

  /** When true, we skip the payment picker and redirect directly to the payment provider (e.g. Worldline). */
  redirectingToPayment = false;

  constructor(
    private readonly router: Router,
    private orderSelector: OrderStoreSelector,
    private basketRefiner: BasketStoreRefiner,
    private basketSelector: BasketStoreSelector,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly paymentService: PaymentService,
    private readonly orderPaymentsApi: OrderPaymentsApiService,
    private readonly ordersService: OrdersService,
    private readonly activatedRoute: ActivatedRoute,
    private paymentDispatcher: PaymentStoreDispatcher,
    private readonly transactionsService: TransactionsService,
    private readonly navigationHistoryService: NavigationHistoryService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      'payment',
      this.activatedRoute.snapshot,
    );

    if (this.router.url.includes('payment_method=EDENRED')) {
      this.orderDispatcher.checkCartIsValid();
    }

    combineLatest([
      this.orderValidate$,
      this.orderSelector.selectErrors,
      this.basketRefiner.selectHasBasket,
    ])
      .pipe(
        takeUntil(this._destroyerRef),
        switchMap(([validate, error, isBasketExist]) => {
          if (!validate && !error) {
            return this.orderDispatcher.checkCartIsValid$();
          }

          if (validate && this.isTransactionPaymentMethodAvailable(validate) && isBasketExist) {
            const basketOrderId = this.basketSelector.basket.order;
            if (basketOrderId) {
              return this.orderSelector.selectOrder(basketOrderId);
            }
            return this.orderSelector.selectActive;
          }

          return of(validate);
        }),
      )
      .subscribe(order => {
        if (this.redirectingToPayment) {
          return;
        }
        if (order) {
          if (OrderUtils.isOrderValidationResponse(order)) {
            const availablePaymentMethods = (order.available_payment_methods ?? [])
              ?.filter(i => i.valid)
              .map(i => i.payment_method)
              .sort((i, j) => i.position - j.position);

            if (
              availablePaymentMethods.length === 1 &&
              REDIRECT_PAYMENT_HANDLES.has(availablePaymentMethods[0].handle)
            ) {
              this.redirectingToPayment = true;
              this.paymentService.setLoadingToStore();
              this.ordersService
                .createOrder$(availablePaymentMethods[0].handle)
                .pipe(
                  take(1),
                  switchMap((createdOrder: DoodOrderModel | null) =>
                    createdOrder
                      ? this.orderPaymentsApi.createPaymentIntent$<{ payment_page_url: string }>(
                          createdOrder.id,
                        )
                      : of(null),
                  ),
                  takeUntil(this._destroyerRef),
                  catchError(err => {
                    this.redirectingToPayment = false;
                    this.paymentService.unSetLoadingToStore();
                    this.orderDispatcher.resetValidation();
                    this.orderDispatcher.updateErrors({ error: err });
                    return of(null);
                  }),
                )
                .subscribe((result: { payment_page_url: string } | null) => {
                  if (result?.payment_page_url) {
                    window.location.href = result.payment_page_url;
                  } else {
                    this.redirectingToPayment = false;
                    this.paymentService.unSetLoadingToStore();
                  }
                });
              return;
            }

            this.paymentDispatcher.updateMethods(availablePaymentMethods);
            this.paymentService.prepareStripeForCheckout(availablePaymentMethods);
          }
        }
        this.paymentService.unSetLoadingToStore();
      });
  }

  ngOnDestroy(): void {
    this.transactionsService.clearActiveTransaction();
    super.ngOnDestroy();
  }

  private isTransactionPaymentMethodAvailable(
    validatedOrder: DoodValidateOrderQuery | null,
  ): boolean {
    if (OrderUtils.isOrderValidationResponse(validatedOrder)) {
      if (!validatedOrder || !validatedOrder.available_payment_methods) return false;
      return !!validatedOrder.available_payment_methods.find(
        method => method.payment_method.handle === PaymentMethodHandles.Transactions,
      );
    }
    return false;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PaymentPageComponent, { 'keyAliases': {} });
